// src/App.jsx
// Main application shell. Manages app-wide state and renders the active tab.
// All feature views live in separate files under ./components/

import { useState, useEffect, useCallback, useRef } from "react";
import * as HS from "./hubspot.js";
import { CSS } from "./styles.js";
import { MAIN_TABS, TAB_LABELS, ADMIN_UN } from "./constants.js";
import { hashPw, sGet, sSet, serverSave, serverLoad } from "./helpers.js";
import * as Summaries from "./summaries.js";
import { bucketForCompany, DEFAULT_ALLOWED_TYPES } from "./companyBuckets.js";

import { Btn, Spin, NavItem, MobTab } from "./components/ui.jsx";
import { LoginScreen, SetPasswordScreen, ChangePwModal, UserMgmt } from "./components/auth.jsx";
import { CompaniesList } from "./components/company.jsx";
import { ContactsList } from "./components/contacts.jsx";
import { DealsPipeline } from "./components/deals.jsx";
import { RoutePlanner } from "./components/route.jsx";
import { CalendarView } from "./components/calendar.jsx";
import { TasksView } from "./components/tasks.jsx";
import { QuotaDashboard, AdminQuotaView } from "./components/quota.jsx";
import { SalesAI } from "./components/salesai.jsx";
import { HubSpotConfig } from "./components/hubspotConfig.jsx";

// Seed admin user on first load (Admin / ABM2026)
function seedAdmin() {
  const existing = sGet("crm_users") || [];
  if (existing.find(u => u.email.toLowerCase() === ADMIN_UN.toLowerCase())) return existing;
  const admin = {
    id: "admin",
    email: ADMIN_UN,
    name: "Administrator",
    passwordHash: hashPw("ABM2026"),
    isTemp: false,
    isAdmin: true,
    seeAll: true,
  };
  const merged = [admin, ...existing];
  sSet("crm_users", merged);
  return merged;
}

// Compare two type arrays (handles both string[] and {value}[] shapes).
// Returns true if they represent the same set of internal values.
function sameTypes(a, b) {
  const norm = arr => (arr || [])
    .map(t => typeof t === "string" ? t : (t.value || t.label || ""))
    .filter(Boolean)
    .sort();
  const A = norm(a), B = norm(b);
  if (A.length !== B.length) return false;
  return A.every((v, i) => v === B[i]);
}

// Friendly "X minutes ago" label
function timeAgo(iso) {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  if (ms < 3_600_000) return Math.floor(ms / 60_000) + " min ago";
  if (ms < 86_400_000) return Math.floor(ms / 3_600_000) + " hr ago";
  return Math.floor(ms / 86_400_000) + " days ago";
}

export default function App() {
  // ── App state ──────────────────────────────────────────────────────────────
  const [appReady,    setAppReady]    = useState(false);
  const [users,       setUsers]       = useState([]);
  const [curUser,     setCurUser]     = useState(null);
  const [needsPwSet,  setNeedsPwSet]  = useState(false);

  const [companies,   setCompanies]   = useState([]);
  const [allowedTypes,setAllowedTypes]= useState([]);
  const [tab,         setTab]         = useState("companies");
  const [loading,     setLoading]     = useState(false);
  const [syncMsg,     setSyncMsg]     = useState("");
  const [syncProg,    setSyncProg]    = useState(null);
  const [enriching,   setEnriching]   = useState(false);
  const [enrichProg,  setEnrichProg]  = useState(null);
  const [lastSync,    setLastSync]    = useState(null);

  const [collapsed,   setCollapsed]   = useState(false);
  const [mobileOpen,  setMobileOpen]  = useState(false);  // mobile drawer state
  const [showUsers,   setShowUsers]   = useState(false);
  const [showPw,      setShowPw]      = useState(false);
  const [showQuota,   setShowQuota]   = useState(false);
  const [showHSCfg,   setShowHSCfg]   = useState(false);
  const [jumpCo,      setJumpCo]      = useState(null);

  // Abort flag for stopping background enrichment
  const enrichAbortRef = useRef({current: false});

  // ── Boot: load users, allowed types, companies ─────────────────────────────
  useEffect(() => {
    (async () => {
      // 1. Users: seed admin, then merge any server-stored users
      const localUsers = seedAdmin();
      const serverUsers = await serverLoad("crm_users");
      let mergedUsers = localUsers;
      if (Array.isArray(serverUsers) && serverUsers.length > 0) {
        const byEmail = new Map();
        [...localUsers, ...serverUsers].forEach(u => {
          const k = (u.email || "").toLowerCase();
          if (k) byEmail.set(k, {...byEmail.get(k), ...u});
        });
        mergedUsers = Array.from(byEmail.values());
        sSet("crm_users", mergedUsers);
      }
      setUsers(mergedUsers);

      // 2. Restore current user session
      const sessUserId = localStorage.getItem("crm_cur_user_id");
      if (sessUserId) {
        const u = mergedUsers.find(x => x.id === sessUserId);
        if (u) {
          setCurUser(u);
          if (u.isTemp) setNeedsPwSet(true);
        }
      }

      // 3. Allowed types
      const localTypes = sGet("crm_allowed_types") || [];
      const serverTypes = await serverLoad("crm_allowed_types");
      const types = (Array.isArray(serverTypes) && serverTypes.length > 0) ? serverTypes : localTypes;
      setAllowedTypes(types);

      // 4. Companies: server first, fall back to localStorage
      const serverCos = await serverLoad("crm_companies");
      if (Array.isArray(serverCos) && serverCos.length > 0) {
        setCompanies(serverCos);
      } else {
        const localCos = sGet("crm_companies") || [];
        setCompanies(localCos);
      }

      // 5. Last sync timestamp
      setLastSync(localStorage.getItem("crm_last_sync"));

      // 6. Discover HubSpot pipelines (kicks off in parallel; updates stage
      // labels and pipeline classification used everywhere deals render).
      HS.discoverPipelines().catch(e => console.warn("Pipeline discovery:", e.message));

      // 7. Load AI summary cache so activity summaries render instantly when
      // contact / company / calendar views open.
      Summaries.loadCache();

      setAppReady(true);
    })();
  }, []);

  // ── Login / logout / password change ───────────────────────────────────────
  const handleLogin = (user) => {
    setCurUser(user);
    localStorage.setItem("crm_cur_user_id", user.id);
    window.__crmUserId = user.id;
    if (user.isTemp) setNeedsPwSet(true);

    if (user.fromHubSpot && !users.find(u => u.email.toLowerCase() === user.email.toLowerCase())) {
      const merged = [...users, user];
      sSet("crm_users", merged);
      setUsers(merged);
      serverSave("crm_users", merged);
    }
  };

  const handleLogout = () => {
    // Stop any background work cleanly
    enrichAbortRef.current.current = true;
    localStorage.removeItem("crm_cur_user_id");
    setCurUser(null);
    setNeedsPwSet(false);
    setTab("companies");
  };

  const handleSetPassword = (newHash) => {
    const updated = users.map(u => u.id === curUser.id ? {...u, passwordHash: newHash, isTemp: false} : u);
    setUsers(updated);
    sSet("crm_users", updated);
    serverSave("crm_users", updated);
    setCurUser({...curUser, passwordHash: newHash, isTemp: false});
    setNeedsPwSet(false);
  };

  const handleChangePassword = (newHash) => {
    const updated = users.map(u => u.id === curUser.id ? {...u, passwordHash: newHash, isTemp: false} : u);
    setUsers(updated);
    sSet("crm_users", updated);
    serverSave("crm_users", updated);
    setCurUser({...curUser, passwordHash: newHash, isTemp: false});
  };

  // ── Pause / cancel enrichment ──────────────────────────────────────────────
  const pauseEnrichment = () => {
    enrichAbortRef.current.current = true;
    setSyncMsg("Background sync paused. Click Sync HubSpot to resume.");
    setTimeout(()=>setSyncMsg(""), 4000);
  };

  // ── Sync from HubSpot ──────────────────────────────────────────────────────
  // Optional `wipeFirst=true` clears existing companies before syncing.
  // Used when types change so old data doesn't linger.
  const syncFromHubSpot = useCallback(async (wipeFirst = false) => {
    if (loading) return;

    // Stop any in-flight enrichment from a previous sync
    enrichAbortRef.current.current = true;
    // Wait one tick for the in-flight loop to notice the abort flag
    await new Promise(r => setTimeout(r, 50));
    // Reset abort for the new run
    enrichAbortRef.current = {current: false};

    setLoading(true);
    setSyncMsg("");
    setSyncProg({loaded: 0, total: 0});

    if (wipeFirst) {
      setCompanies([]);
      sSet("crm_companies", []);
      try { await serverSave("crm_companies", []); } catch {}
    }

    try {
      const cos = await HS.getCompanies(allowedTypes, (loaded, total) => {
        setSyncProg({loaded, total});
      });
      setCompanies(cos);
      sSet("crm_companies", cos);
      try { await serverSave("crm_companies", cos); } catch {}
      const ts = new Date().toISOString();
      localStorage.setItem("crm_last_sync", ts);
      setLastSync(ts);
      setSyncMsg("Synced " + cos.length + " companies. Loading deals & contacts in background...");

      // Phase 2: enrichment runs detached, throttled, with abort support
      setEnriching(true);
      setEnrichProg({done: 0, total: cos.length});

      let lastServerSaveAt = 0;
      let lastLocalSaveAt = 0;
      HS.enrichCompanies(cos, (enriched, done, total, aborted) => {
        setCompanies([...enriched]);
        setEnrichProg({done, total});
        // Throttle BOTH localStorage and Azure Blob saves: every 100 companies
        // or at completion/abort. localStorage of 4,000+ companies is ~5-15 MB
        // of JSON -- writing it every batch blocks the main thread for hundreds
        // of milliseconds on mobile, which is the root cause of scroll lag.
        const finished = done >= total || aborted;
        const stride = done - lastServerSaveAt >= 100;
        if (finished || stride) {
          lastServerSaveAt = done;
          serverSave("crm_companies", enriched).catch(()=>{});
        }
        if (finished || done - lastLocalSaveAt >= 100) {
          lastLocalSaveAt = done;
          // Use a microtask delay so the UI repaints before the heavy write
          setTimeout(() => sSet("crm_companies", enriched), 0);
        }
        if (finished) {
          setEnriching(false);
          setEnrichProg(null);
          setSyncMsg(aborted
            ? "Background sync paused at " + done + " of " + total + "."
            : "Fully synced - " + total + " companies with deals & contacts loaded ✓"
          );
          setTimeout(()=>setSyncMsg(""), 5000);
        }
      }, enrichAbortRef.current).catch(e => {
        console.warn("Enrichment failed:", e.message);
        setEnriching(false);
        setEnrichProg(null);
      });
    } catch (e) {
      setSyncMsg("Sync error: " + e.message);
      setTimeout(()=>setSyncMsg(""), 5000);
    }
    setLoading(false);
    setSyncProg(null);
  }, [loading, allowedTypes]);

  // ── Type / company / user updates ──────────────────────────────────────────
  // When types change, wipe stored companies and start fresh.
  const handleSaveTypes = (newTypes) => {
    const typesChanged = !sameTypes(allowedTypes, newTypes);

    setAllowedTypes(newTypes);
    sSet("crm_allowed_types", newTypes);
    serverSave("crm_allowed_types", newTypes);
    setShowHSCfg(false);

    // Wipe companies if types actually changed -- prevents stale data leaking through
    syncFromHubSpot(typesChanged);
  };

  const handleUpdateCompany = (updated) => {
    // Upsert: replace existing match by id, or append if new (e.g. just-
    // created via AddCompanyModal).
    const existsIdx = companies.findIndex(c => c.id === updated.id);
    const next = existsIdx === -1
      ? [updated, ...companies]
      : companies.map(c => c.id === updated.id ? updated : c);
    setCompanies(next);
    sSet("crm_companies", next);
    serverSave("crm_companies", next);
    if (updated.ownerId !== undefined) {
      HS.updateCompanyOwner(updated.id, updated.ownerId).catch(e => console.warn("Owner update:", e.message));
    }
  };

  const handleUpdateCompanies = (next) => {
    setCompanies(next);
    sSet("crm_companies", next);
    serverSave("crm_companies", next);
  };

  const handleUsersChange = (next) => {
    setUsers(next);
    sSet("crm_users", next);
    serverSave("crm_users", next);
  };

  // ── Push to Mobile (force a fresh blob save) ───────────────────────────────
  const pushToMobile = async () => {
    setSyncMsg("Pushing to mobile...");
    try {
      await Promise.all([
        serverSave("crm_companies", companies),
        serverSave("crm_users", users),
        serverSave("crm_allowed_types", allowedTypes),
      ]);
      setSyncMsg("Pushed to mobile ✓ Reps can now sync on their phones.");
      setTimeout(()=>setSyncMsg(""), 4000);
    } catch (e) {
      setSyncMsg("Push failed: " + e.message);
      setTimeout(()=>setSyncMsg(""), 4000);
    }
  };

  // ── Render guards ──────────────────────────────────────────────────────────
  if (!appReady) {
    return (
      <>
        <style>{CSS}</style>
        <div style={{minHeight:"100vh",background:"var(--cream)",display:"flex",alignItems:"center",justifyContent:"center"}}>
          <Spin/> <span style={{marginLeft:10,color:"var(--muted)"}}>Loading ABM CRM...</span>
        </div>
      </>
    );
  }

  if (!curUser) {
    return (
      <>
        <style>{CSS}</style>
        <LoginScreen users={users} onLogin={handleLogin}/>
      </>
    );
  }

  if (needsPwSet) {
    return (
      <>
        <style>{CSS}</style>
        <SetPasswordScreen user={curUser} onChanged={handleSetPassword}/>
      </>
    );
  }

  // ── Filter companies for non-admin reps without seeAll ─────────────────────
  // Two stacked filters (admin bypasses both):
  //   1. Ownership/seeAll filter -- existing behavior. Rep without seeAll sees
  //      only companies whose owner matches their HubSpot ID.
  //   2. userAllowedTypes filter -- new. Restricts to buckets admin granted.
  //      Default for new users: all buckets allowed.
  //
  // Note: there's a separate top-level `allowedTypes` state (line 72) that
  // controls which company TYPES the app fetches from HubSpot at the tenant
  // level (admin config). That's different from this PER-USER bucket filter.
  const userAllowedTypes = curUser.isAdmin
    ? null  // null = no restriction (admin override)
    : (Array.isArray(curUser.allowedTypes) ? curUser.allowedTypes : DEFAULT_ALLOWED_TYPES);

  const visibleCompanies = curUser.isAdmin
    ? companies
    : companies.filter(c => {
        // Step 1: ownership/seeAll check
        if (!curUser.seeAll) {
          const ownerMatch = c.ownerId === curUser.id
            || c.ownerId === curUser.hubspotOwnerId
            || c.ownerId === ((curUser.id || "").startsWith("hs_") ? curUser.id.slice(3) : null);
          if (!ownerMatch) return false;
        }
        // Step 2: bucket restriction -- if rep's userAllowedTypes excludes
        // this company's bucket, hide it. Empty array = total lockout (no
        // companies visible) which is intentional -- admin can use this to
        // suspend a rep without removing them.
        if (userAllowedTypes && !userAllowedTypes.includes(bucketForCompany(c))) return false;
        return true;
      });

  // ── Main app ───────────────────────────────────────────────────────────────
  return (
    <>
      <style>{CSS}</style>
      <div style={{minHeight:"100vh",background:"var(--cream)",display:"flex"}}>

        {/* MOBILE HAMBURGER -- only visible at <=768px via CSS. Resets collapse
            so labels are visible when drawer opens, even if user collapsed
            sidebar on desktop earlier in the session. */}
        <button className="mobile-menu-btn" onClick={()=>{setCollapsed(false); setMobileOpen(true);}} aria-label="Open menu">
          ☰
        </button>

        {/* MOBILE BACKDROP -- darkens main content when drawer is open. Tap to close. */}
        <div className="mobile-backdrop" data-open={mobileOpen ? "true" : "false"} onClick={()=>setMobileOpen(false)} />

        {/* SIDEBAR (desktop) -- on mobile, becomes a slide-in drawer (controlled via data-open) */}
        <aside className="desk-sb" data-open={mobileOpen ? "true" : "false"}
          onClick={e => {
            // On mobile, any click on a nav button inside the drawer closes
            // the drawer (so tapping a tab actually shows the content).
            // Buttons marked data-keep-open (e.g. the collapse toggle) opt out.
            if (window.innerWidth <= 768 && e.target.closest("button:not([data-keep-open])")) {
              setMobileOpen(false);
            }
          }}
          style={{
          width: collapsed ? 64 : 240,
          background: "#1a1a1a",
          color: "#fff",
          padding: "20px 12px",
          display: "flex",
          flexDirection: "column",
          transition: "width .25s",
          flexShrink: 0,
          position: "sticky",
          top: 0,
          height: "100vh",
        }}>
          {/* Brand */}
          <div style={{padding:"0 4px 16px",borderBottom:"1px solid rgba(255,255,255,.08)",marginBottom:16,display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:36,height:36,borderRadius:10,background:"linear-gradient(135deg,#b01020,#222222)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>🛏</div>
            {!collapsed && (
              <div>
                <div style={{fontFamily:"Barlow Condensed,sans-serif",fontSize:18,fontWeight:700,letterSpacing:.5}}>ABM CRM</div>
                <div style={{fontSize:10,color:"rgba(255,255,255,.4)"}}>Powered by HubSpot</div>
              </div>
            )}
          </div>

          {/* Main nav */}
          <div style={{flex:1,overflowY:"auto"}}>
            {MAIN_TABS.filter(t => !t.adminOnly || curUser.isAdmin).map(t => (
              <NavItem key={t.id} icon={t.icon} label={t.label} active={tab === t.id}
                onClick={()=>{setTab(t.id); setJumpCo(null);}} collapsed={collapsed}/>
            ))}

            {/* Admin-only nav */}
            {curUser.isAdmin && (
              <div style={{marginTop:14,paddingTop:12,borderTop:"1px solid rgba(255,255,255,.08)"}}>
                <NavItem icon="⚙" label="HubSpot Settings" tooltip="HubSpot Settings" onClick={()=>setShowHSCfg(true)} collapsed={collapsed}/>
                <NavItem icon="📊" label="Quota Report" tooltip="Quota Report" onClick={()=>setShowQuota(true)} collapsed={collapsed}/>
                <NavItem icon="👥" label="Manage Users" tooltip="Manage Users" onClick={()=>setShowUsers(true)} collapsed={collapsed}/>
              </div>
            )}
          </div>

          {/* Footer actions */}
          <div style={{paddingTop:12,borderTop:"1px solid rgba(255,255,255,.08)"}}>
            <NavItem
              icon="↻"
              label={loading ? "Syncing..." : enriching ? "Enriching..." : "Sync HubSpot"}
              tooltip="Sync HubSpot"
              onClick={()=>syncFromHubSpot(false)}
              collapsed={collapsed}
            />
            {enriching && (
              <NavItem icon="⏸" label="Pause Sync" tooltip="Pause background sync" onClick={pauseEnrichment} collapsed={collapsed}/>
            )}
            {curUser.isAdmin && <NavItem icon="📲" label="Push to Mobile" tooltip="Push to Mobile" onClick={pushToMobile} collapsed={collapsed}/>}
            <NavItem icon="🔐" label="Change Password" tooltip="Change Password" onClick={()=>setShowPw(true)} collapsed={collapsed}/>
            <NavItem icon="↪" label="Sign Out" tooltip="Sign Out" onClick={handleLogout} collapsed={collapsed}/>

            {/* Last sync indicator */}
            {!collapsed && lastSync && (
              <div style={{padding:"8px 12px",fontSize:10,color:"rgba(255,255,255,.4)",lineHeight:1.5}}>
                Last sync: {timeAgo(lastSync)}
              </div>
            )}

            {/* Collapse toggle -- desktop-only behavior; on mobile use the
                backdrop or a nav item tap to close. data-keep-open prevents
                the parent aside's onClick from closing the drawer when this
                button is tapped. */}
            <button onClick={()=>setCollapsed(c => !c)} data-keep-open="true"
              style={{width:"100%",marginTop:6,padding:"8px 0",borderRadius:10,background:"rgba(255,255,255,.05)",border:"none",color:"rgba(255,255,255,.5)",fontSize:14,cursor:"pointer",fontFamily:"Barlow,sans-serif"}}>
              {collapsed ? "›" : "‹"}
            </button>
          </div>
        </aside>

        {/* MAIN CONTENT */}
        <main style={{flex:1,minWidth:0}}>
          <div className="main-pad" style={{padding:"30px 40px"}}>
            {/* Header */}
            <div style={{marginBottom:24,display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
              <div>
                <h1 style={{fontSize:28,fontWeight:700,color:"var(--dark)"}}>{TAB_LABELS[tab] || tab}</h1>
                {tab === "companies" && (
                  <div style={{fontSize:13,color:"var(--muted)",marginTop:2}}>
                    {visibleCompanies.length} total {visibleCompanies.length === 1 ? "company" : "companies"}
                    {!curUser.isAdmin && !curUser.seeAll && " (yours)"}
                  </div>
                )}
              </div>
              <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                {syncMsg && (
                  <span style={{
                    fontSize:13,
                    color:syncMsg.includes("error") || syncMsg.includes("failed") ? "var(--red)" : "var(--green)",
                    fontWeight:600,padding:"6px 14px",borderRadius:99,
                    background:syncMsg.includes("error") || syncMsg.includes("failed") ? "#fdecea" : "#e6f4ed",
                  }}>{syncMsg}</span>
                )}
                <span style={{fontSize:12,color:"var(--muted)"}}>👤 {curUser.name || curUser.email}{curUser.isAdmin && " (Admin)"}</span>
              </div>
            </div>

            {/* Sync progress bar (Phase 1) */}
            {loading && syncProg && (
              <div style={{marginBottom:18,padding:"10px 16px",borderRadius:10,background:"var(--warm1)",border:"1px solid var(--warm2)",display:"flex",alignItems:"center",gap:10,fontSize:13}}>
                <Spin/>
                <span style={{flex:1}}>Fetching... {syncProg.loaded} of ~{syncProg.total} companies</span>
              </div>
            )}

            {/* Enrichment progress bar (Phase 2 - background) */}
            {enriching && enrichProg && (
              <div style={{marginBottom:18,padding:"10px 16px",borderRadius:10,background:"#fff8e6",border:"1px solid #fde68a",display:"flex",alignItems:"center",gap:10,fontSize:13}}>
                <Spin/>
                <div style={{flex:1}}>
                  <div>Loading deals &amp; contacts in background ({enrichProg.done} of {enrichProg.total})</div>
                  <div style={{height:4,borderRadius:99,background:"rgba(0,0,0,.08)",marginTop:6,overflow:"hidden"}}>
                    <div style={{height:"100%",width:(enrichProg.total ? (enrichProg.done / enrichProg.total * 100) : 0) + "%",background:"#e8a838",transition:"width .5s"}}/>
                  </div>
                </div>
                <button onClick={pauseEnrichment}
                  style={{padding:"4px 12px",borderRadius:99,border:"1px solid #fde68a",background:"#fff",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"Barlow,sans-serif",color:"#8a5c10"}}>
                  ⏸ Pause
                </button>
              </div>
            )}

            {/* Active tab */}
            {tab === "companies" && (
              <CompaniesList
                companies={visibleCompanies}
                users={users}
                currentUser={curUser}
                loading={loading && companies.length === 0}
                onUpdate={handleUpdateCompany}
                initialOpen={jumpCo}
              />
            )}
            {tab === "contacts" && (
              <ContactsList
                companies={visibleCompanies}
                users={users}
                currentUser={curUser}
                onUpdate={handleUpdateCompany}
                onOpenCompany={(co)=>{setJumpCo(co); setTab("companies");}}
              />
            )}
            {tab === "pipeline" && (
              <DealsPipeline
                companies={visibleCompanies}
                currentUser={curUser}
                onOpenCompany={(co)=>{setJumpCo(co); setTab("companies");}}
                onUpdateCompanies={handleUpdateCompanies}
              />
            )}
            {tab === "route"    && <RoutePlanner    companies={visibleCompanies}/>}
            {tab === "calendar" && <CalendarView    currentUser={curUser} users={users} companies={visibleCompanies}/>}
            {tab === "tasks"    && <TasksView       currentUser={curUser} users={users} companies={visibleCompanies}/>}
            {tab === "quota"    && <QuotaDashboard  currentUser={curUser} users={users}/>}
            {/* Sales AI is admin-only -- gates AI spend to a single user.
                Even if a non-admin's tab state somehow lands here (stale
                localStorage, deep link, etc.), block render and show a
                friendly message rather than firing AI calls. */}
            {tab === "salesai"  && (curUser.isAdmin
              ? <SalesAI companies={visibleCompanies} currentUser={curUser}/>
              : (
                <div style={{padding:60,textAlign:"center",animation:"fadeUp .3s ease both"}}>
                  <div style={{fontSize:42,marginBottom:14,opacity:.4}}>🔒</div>
                  <div style={{fontSize:15,fontWeight:600,color:"var(--brown)",marginBottom:6}}>Admin only</div>
                  <div style={{fontSize:13,color:"var(--muted)"}}>Sales AI is currently restricted to administrators.</div>
                </div>
              )
            )}
          </div>
        </main>

        {/* MOBILE NAV */}
        <nav className="mobile-nav">
          {MAIN_TABS.slice(0, 5).map(t => (
            <MobTab key={t.id} icon={t.icon} label={t.label} active={tab === t.id} onClick={()=>{setTab(t.id); setJumpCo(null);}}/>
          ))}
        </nav>
      </div>

      {/* Modals */}
      {showUsers && <UserMgmt        users={users} onUsersChange={handleUsersChange} onClose={()=>setShowUsers(false)}/>}
      {showPw    && <ChangePwModal   user={curUser} onClose={()=>setShowPw(false)} onChanged={handleChangePassword}/>}
      {showQuota && <AdminQuotaView  users={users} onClose={()=>setShowQuota(false)}/>}
      {showHSCfg && <HubSpotConfig
        allowedTypes={allowedTypes}
        onSave={handleSaveTypes}
        onClose={()=>setShowHSCfg(false)}
        existingUsers={users}
        onUsersChange={handleUsersChange}
      />}
    </>
  );
}
